import React, { useState } from 'react';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { useAdminSystemAlerts, useDeleteAdminSystemAlert } from '../../hooks/useAdmin';
import { AnnouncementCard } from '../pulse/AnnouncementCard';
import { Spinner } from '../ui/Spinner';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Megaphone, ShieldAlert, Trash2, Users, ExternalLink, ChevronLeft, ChevronRight, AlertTriangle, Eye, CheckCircle2, Globe, Building2, Lock, Info } from 'lucide-react';
import { SystemAlert } from '../../types';
import { BroadcastRecipientsModal } from './BroadcastRecipientsModal';

const isGlobalAlert = (nodeId?: string) => {
  if (!nodeId) return true;
  return ['platform-root', 'root', 'global', 'all'].includes(nodeId.toLowerCase().trim());
};

export const AdminGlobalBroadcasts: React.FC = () => {
  const [alertPage, setAlertPage] = useState(0);
  const [filterScope, setFilterScope] = useState<'ALL' | 'GLOBAL' | 'LOCAL'>('ALL');
  const [selectedBroadcast, setSelectedBroadcast] = useState<SystemAlert | null>(null);
  const { data: alertsData, isLoading: isLoadingAlerts } = useAdminSystemAlerts(alertPage, 6);
  const deleteAlertMutation = useDeleteAdminSystemAlert();

  const { data: rawAnnouncements, isLoading: isLoadingAnnouncements } = useAnnouncements('platform-root', false);
  const announcements = Array.isArray(rawAnnouncements) ? rawAnnouncements : [];

  const alerts: SystemAlert[] = alertsData?.content || [];
  const totalAlertPages = alertsData?.totalPages || 1;

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const filteredAlerts = alerts.filter(alert => {
    const isGlobal = isGlobalAlert(alert.nodeId);
    if (filterScope === 'GLOBAL') return isGlobal;
    if (filterScope === 'LOCAL') return !isGlobal;
    return true;
  });

  const globalCount = alerts.filter(a => isGlobalAlert(a.nodeId)).length;
  const localCount = alerts.filter(a => !isGlobalAlert(a.nodeId)).length;

  const handleDeleteAlert = (alertId: string) => {
    deleteAlertMutation.mutate(alertId, {
      onSettled: () => setConfirmDeleteId(null)
    });
  };

  return (
    <div className="space-y-8">
      {/* 1. Admin Broadcasts & Org Pulses Section */}
      <section className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b-2 border-black dark:border-white pb-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Megaphone className="text-[#0044FF]" size={22} />
              <h3 className="font-display font-black uppercase tracking-tight text-lg text-gray-900 dark:text-white">
                Platform & Tenant Broadcasts
              </h3>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Platform-wide broadcasts sent to all users and monitored local pulses published across organizations.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="blue" className="font-mono text-[10px] tracking-widest uppercase self-start sm:self-auto">
              {alertsData?.totalElements ?? 0} TOTAL
            </Badge>
          </div>
        </div>

        {/* Filter Tabs */}
        {alerts.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <button
              type="button"
              onClick={() => setFilterScope('ALL')}
              className={`px-3 py-1 font-mono text-xs font-bold uppercase tracking-wider transition-colors border-2 border-black dark:border-white ${
                filterScope === 'ALL'
                  ? 'bg-black text-white dark:bg-white dark:text-black shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]'
                  : 'bg-white text-gray-700 dark:bg-slate-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800'
              }`}
            >
              All ({alerts.length})
            </button>
            <button
              type="button"
              onClick={() => setFilterScope('GLOBAL')}
              className={`px-3 py-1 font-mono text-xs font-bold uppercase tracking-wider transition-colors border-2 border-black dark:border-white flex items-center gap-1.5 ${
                filterScope === 'GLOBAL'
                  ? 'bg-[#0044FF] text-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]'
                  : 'bg-white text-gray-700 dark:bg-slate-900 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-950/30'
              }`}
            >
              <Globe size={13} /> Global Broadcasts ({globalCount})
            </button>
            <button
              type="button"
              onClick={() => setFilterScope('LOCAL')}
              className={`px-3 py-1 font-mono text-xs font-bold uppercase tracking-wider transition-colors border-2 border-black dark:border-white flex items-center gap-1.5 ${
                filterScope === 'LOCAL'
                  ? 'bg-amber-600 text-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]'
                  : 'bg-white text-gray-700 dark:bg-slate-900 dark:text-gray-300 hover:bg-amber-50 dark:hover:bg-amber-950/30'
              }`}
            >
              <Building2 size={13} /> Local Org Pulses ({localCount})
            </button>
          </div>
        )}

        {isLoadingAlerts ? (
          <div className="flex justify-center p-8">
            <Spinner size="md" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center p-8 text-gray-500 border-2 border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-slate-900/50">
            <Megaphone size={32} className="mx-auto mb-2 opacity-40 text-gray-400" />
            <p className="font-medium text-sm">No active broadcasts or alerts.</p>
            <p className="text-xs text-gray-400 mt-1">Platform broadcasts and monitored organization alerts will appear here.</p>
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className="text-center p-8 text-gray-500 border-2 border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-slate-900/50">
            <p className="font-medium text-sm">No broadcasts match the selected "{filterScope}" filter.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredAlerts.map((alert) => {
                const isGlobal = isGlobalAlert(alert.nodeId);
                const isUrgent = alert.type === 'ALERT' || alert.type === 'EMERGENCY' || alert.type === 'SYSTEM_ALERT';

                return (
                  <div
                    key={alert.id}
                    className={`flex flex-col justify-between border-2 border-black dark:border-white bg-white dark:bg-slate-900 p-5 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] relative ${
                      !isGlobal ? 'border-l-8 border-l-amber-500' : 'border-l-8 border-l-[#0044FF]'
                    }`}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          {isGlobal ? (
                            <Badge variant={isUrgent ? "red" : "blue"} className="font-mono text-[10px] uppercase tracking-wider flex items-center gap-1">
                              <Globe size={11} />
                              {isUrgent ? 'URGENT GLOBAL BROADCAST' : 'GLOBAL BROADCAST'}
                            </Badge>
                          ) : (
                            <Badge variant="amber" className="font-mono text-[10px] uppercase tracking-wider flex items-center gap-1 border border-black/20 dark:border-white/20 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200">
                              <Building2 size={11} className="text-amber-600 dark:text-amber-400" />
                              {isUrgent ? 'URGENT ORG PULSE' : 'LOCAL ORG PULSE'}
                            </Badge>
                          )}

                          <div className="flex items-center gap-1 text-[11px] font-mono font-bold">
                            {isGlobal ? (
                              <span className="text-blue-600 dark:text-blue-400">
                                Scope: Platform-Wide (All Users)
                              </span>
                            ) : (
                              <span className="text-gray-700 dark:text-gray-300 flex items-center gap-1.5 flex-wrap">
                                <Lock size={11} className="text-amber-600 dark:text-amber-400 shrink-0" />
                                <span>Private to: <strong className="font-bold text-gray-900 dark:text-white">{alert.nodeName || 'Local Organization'}</strong></span>
                                <span className="font-mono text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-slate-800 px-1 py-0.5 border border-black/10 dark:border-white/10" title={`Node ID: ${alert.nodeId}`}>
                                  ID: {alert.nodeId.length > 12 ? `${alert.nodeId.slice(0, 8)}...` : alert.nodeId}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setSelectedBroadcast(alert)}
                          className="flex items-center gap-1.5 text-xs font-mono font-bold text-gray-700 dark:text-gray-300 hover:text-[#0044FF] dark:hover:text-blue-400 bg-gray-100 dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700/60 px-2 py-0.5 border border-black/20 dark:border-white/20 transition-colors cursor-pointer group rounded-none"
                          title="Click to view who has read this broadcast"
                        >
                          <CheckCircle2 size={13} className="text-emerald-600 dark:text-emerald-400 group-hover:text-[#0044FF]" />
                          <span className="underline decoration-dotted underline-offset-2">
                            {alert.readCount ?? 0} read
                          </span>
                          <Eye size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#0044FF]" />
                        </button>
                      </div>

                      {!isGlobal && (
                        <div className="flex items-start gap-1.5 bg-amber-50/80 dark:bg-amber-950/20 border-l-2 border-amber-500 p-2 text-[11px] text-amber-900 dark:text-amber-200">
                          <Info size={13} className="text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold">Private Org Alert:</span> Visible <u>only</u> to members of <strong className="font-bold">{alert.nodeName ? alert.nodeName : 'this organization'}</strong> (<code className="font-mono text-[10px] text-gray-500">{alert.nodeId}</code>). This was <strong>not</strong> broadcast platform-wide.
                          </div>
                        </div>
                      )}

                      <div>
                        <h4 className="font-display font-black text-base text-gray-900 dark:text-white leading-snug">
                          {alert.title}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-wrap line-clamp-3">
                          {alert.message}
                        </p>
                      </div>

                      {(alert.link || (alert as any).linkUrl) && (
                        <div className="pt-1">
                          <a
                            href={alert.link || (alert as any).linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-mono font-bold text-[#0044FF] hover:underline"
                          >
                            <ExternalLink size={12} /> {alert.link || (alert as any).linkUrl}
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
                      <span className="text-[10px] font-mono text-gray-400">
                        {new Date(alert.createdAt).toLocaleString()}
                      </span>

                      {confirmDeleteId === alert.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-bold text-red-600">
                            {isGlobal ? 'Revoke across all users?' : 'Remove alert for this org?'}
                          </span>
                          <Button
                            size="sm"
                            variant="danger"
                            isLoading={deleteAlertMutation.isPending}
                            onClick={() => handleDeleteAlert(alert.id)}
                            className="rounded-none text-xs font-bold uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white px-2.5 py-1"
                          >
                            Confirm
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setConfirmDeleteId(null)}
                            className="rounded-none text-xs px-2 py-1"
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setConfirmDeleteId(alert.id)}
                          className="rounded-none border-2 border-black dark:border-white hover:bg-red-50 hover:border-red-600 hover:text-red-600 dark:hover:bg-red-950/30 text-xs font-bold uppercase tracking-wider gap-1.5 px-3 py-1"
                        >
                          <Trash2 size={13} /> {isGlobal ? 'Revoke Broadcast' : 'Remove Org Alert'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {totalAlertPages > 1 && (
              <div className="flex items-center justify-between pt-2">
                <span className="text-xs font-mono text-gray-500">
                  Page {alertPage + 1} of {totalAlertPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={alertPage === 0}
                    onClick={() => setAlertPage(p => Math.max(0, p - 1))}
                    className="rounded-none border-2 border-black dark:border-white p-1"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={alertPage >= totalAlertPages - 1}
                    onClick={() => setAlertPage(p => p + 1)}
                    className="rounded-none border-2 border-black dark:border-white p-1"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* 2. Pulse Global Announcements Section */}
      <section className="space-y-4 pt-4">
        <div className="flex items-center justify-between border-b-2 border-black dark:border-white pb-3">
          <div className="flex items-center gap-2">
            <Megaphone className="text-blue-600" size={20} />
            <h3 className="font-display font-black uppercase tracking-tight text-lg text-gray-900 dark:text-white">
              Pulse Global Announcements
            </h3>
          </div>
          <Badge variant="blue" className="font-mono text-[10px] tracking-widest uppercase">
            {announcements.length} POSTED
          </Badge>
        </div>

        {isLoadingAnnouncements ? (
          <div className="flex justify-center p-8">
            <Spinner size="md" />
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center p-8 text-gray-500 border-2 border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-slate-900/50">
            <Megaphone size={32} className="mx-auto mb-2 opacity-40 text-gray-400" />
            <p className="font-medium text-sm">No active global announcements.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {announcements.map((announcement) => (
              <AnnouncementCard 
                key={announcement.id} 
                announcement={announcement} 
                isAdminView={true}
              />
            ))}
          </div>
        )}
      </section>

      {/* Broadcast Recipients & Read Receipts Drilldown Modal */}
      {selectedBroadcast && (
        <BroadcastRecipientsModal
          broadcast={selectedBroadcast}
          onClose={() => setSelectedBroadcast(null)}
        />
      )}
    </div>
  );
};
